import { DatePipe, DecimalPipe, UpperCasePipe } from "@angular/common";
import { Component, computed, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from "@angular/router";
import { ImageUrlDirective } from "@app/directives";
import {
  type CustomerCartView,
  CustomerCartViewComponent,
} from "@components/customer-cart-view/customer-cart-view.component";
import { FormAutocompleteComponent, FormAutocompleteOption } from "@components/index";
import { IApiResponse } from "@models/api-response.model";
import { AppStateService } from "@services/app-state/app-state.service";
import { RequestService } from "@services/request/request.service";

@Component({
  selector: "fh-user-details",
  templateUrl: "./user-details.component.html",
  styleUrls: ["./user-details.component.scss"],
  standalone: true,
  imports: [
    DatePipe,
    UpperCasePipe,
    DecimalPipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatCheckboxModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    FormAutocompleteComponent,
    ImageUrlDirective,
    CustomerCartViewComponent,
  ],
})
export class UserDetailsComponent implements OnInit {
  id = signal<number>(0);
  isLoading = signal<boolean>(false);
  isSaving = signal<boolean>(false);

  // Forms
  contactForm!: FormGroup;
  quizForm!: FormGroup;
  remarksForm!: FormGroup;

  // User data
  created_on = signal<string>("");
  updated_on = signal<string>("");
  lastAccess = signal<string>("");
  address = signal<string>("");
  postalCode = signal<string>("");
  access = signal<string>("");
  /** This shopper's last 50 searches, newest first; read-only. */
  recentSearches = signal<RecentSearch[]>([]);
  /** Searches the shopper asked to be notified about ("Notify me when found"). */
  fishHunts = signal<FishHunt[]>([]);
  /** The six fishkeeper identity badges, and which of them this user has. */
  readonly badgeOptions = FISHKEEPER_BADGES;
  badges = signal<string[]>([]);
  status = signal<string>("");
  isSavingStatus = signal<boolean>(false);
  /** When they unsubscribed from Email Outreach; null when subscribed. */
  outreachUnsubscribedAt = signal<string | null>(null);
  isSavingOutreach = signal<boolean>(false);

  // Cart data
  /** The cart as the customer sees it, or null when they have none. */
  cart = signal<CustomerCartView | null>(null);

  // Order history
  orders = signal<IOrderRow[]>([]);
  isLoadingOrders = signal<boolean>(false);
  orderColumns = [
    "id",
    "iso2",
    "currency",
    "sub_total",
    "shipping_total",
    "destination_transhipper_markup",
    "offerDiscountAmount",
    "deliveryDiscountAmount",
    "total",
    "created_on",
    "payment_type",
    "payment_status",
    "order_status",
  ];

  // Phone country code options - computed from stable allCountries signal
  // Using memoization to avoid recomputing for large lists
  phoneCountryCodeOptions = computed<FormAutocompleteOption<string>[]>(() => {
    const countries = this.appStateService.allCountries();
    if (!countries || countries.length === 0) {
      return [];
    }
    // Map all countries (no slice restriction)
    return countries.map((country) => ({
      value: `+${country.phonecode}`,
      label: `${country.name} (+${country.phonecode})`,
      displayLabel: `+${country.phonecode}`,
    }));
  });

  // Country (iso2) options
  countryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    const countries = this.appStateService.allCountries();
    if (!countries || countries.length === 0) return [];
    return countries.map((country) => ({
      value: country.iso2.toUpperCase(),
      label: country.name,
      displayLabel: `${country.iso2.toUpperCase()} — ${country.name}`,
    }));
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: RequestService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private appStateService: AppStateService
  ) {}

  goBack(): void {
    this.router.navigate(["/user_list"]);
  }

  // An admin can't activate/deactivate their own account — same guard the
  // backend enforces; this just keeps the control visibly disabled instead
  // of only failing after a click.
  get isOwnAccount(): boolean {
    return this.appStateService.currentUser$.value?.id === this.id();
  }

  /** Re-subscribe to Email Outreach, or unsubscribe - at the customer's request. */
  setOutreachSubscribed(subscribed: boolean): void {
    this.isSavingOutreach.set(true);
    this.auth
      .post<IApiResponse>("/api/v2/email-outreach/subscription", { userId: this.id(), subscribed })
      .subscribe({
        next: (res) => {
          this.isSavingOutreach.set(false);
          if (res.status) {
            this.outreachUnsubscribedAt.set(subscribed ? null : new Date().toISOString());
          }
          this.snackBar.open(res.message || (res.status ? "Saved" : "Not saved"), "Close", {
            duration: 4000,
          });
        },
        error: () => {
          this.isSavingOutreach.set(false);
          this.snackBar.open("The subscription could not be changed", "Close", { duration: 4000 });
        },
      });
  }

  setStatus(newStatus: "active" | "inactive"): void {
    if (this.isOwnAccount || this.isSavingStatus()) return;

    this.isSavingStatus.set(true);
    this.auth.patch("/api/v1/user", { userId: this.id(), status: newStatus }).subscribe({
      next: (res: any) => {
        if (res.result_code === 1) {
          this.status.set(newStatus);
          this.showMessage(newStatus === "active" ? "Account activated" : "Account deactivated");
        } else {
          this.showMessage(res.message || "Failed to update status");
        }
        this.isSavingStatus.set(false);
      },
      error: (error) => {
        console.error("Error updating status:", error);
        this.showMessage("Error updating status");
        this.isSavingStatus.set(false);
      },
    });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get("id");
    const userId = idParam ? parseInt(idParam, 10) : 0;

    if (!userId || Number.isNaN(userId)) {
      console.error("Invalid user ID:", idParam);
      this.showMessage("Invalid user ID");
      return;
    }

    this.id.set(userId);
    this.initializeForms();
    this.fetchUser();
    this.fetchCart();
    this.fetchOrders();
  }

  private initializeForms(): void {
    this.contactForm = this.fb.group({
      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      phoneCountryCode: ["", Validators.required],
      phoneNumber: ["", Validators.required],
      iso2: [""],
    });

    this.quizForm = this.fb.group({
      userInformation: [""],
      tankCapacity: [""],
    });

    this.remarksForm = this.fb.group({
      adminRemarks: [""],
      adminFollowup: [""],
    });
  }

  fetchUser(): void {
    this.isLoading.set(true);
    this.auth.get<IApiResponse>(`/api/v1/user_details?id=${this.id()}`).subscribe({
      next: (res) => {
        if (res.result_code === 1) {
          this.recentSearches.set(res.data.recentSearches ?? []);
          this.fishHunts.set(res.data.fishHunts ?? []);
          this.badges.set(res.data.badges ?? []);

          this.applyContact(res.data);
          this.contactForm.patchValue({
            iso2: res.data.iso2 ? res.data.iso2.toUpperCase() : "",
          });
          this.applyQuiz(res.data);
          this.applyRemarks(res.data);

          // Set other fields
          this.created_on.set(res.data.created_on || "");
          this.updated_on.set(res.data.updated_on || "");
          this.address.set(res.data.address || "");
          this.postalCode.set(res.data.postal_code || "");
          this.access.set(res.data.access || "");
          this.outreachUnsubscribedAt.set(res.data.outreach_unsubscribed_at || null);
          this.status.set(res.data.status || "");
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error("Error fetching user:", error);
        this.showMessage("Error loading user details");
        this.isLoading.set(false);
      },
    });
  }

  fetchCart(): void {
    this.auth.get<IUserCartApiResponse>(`/api/v1/get-user-cart?id=${this.id()}`).subscribe({
      next: (res) => {
        if (res.result_code === 1) {
          this.cart.set(res.data?.cart ?? null);
        }
      },
      error: (error) => {
        console.error("Error fetching cart:", error);
      },
    });
  }

  // Each section's save updates just that section from the reply (the updated
  // user row): a full fetchUser() would wipe unsaved edits in the other sections.

  private applyContact(data: any): void {
    this.contactForm.patchValue({
      firstName: data.first_name || "",
      lastName: data.last_name || "",
      email: data.email || "",
      phoneCountryCode: data.phone_country_code || "",
      phoneNumber: data.phone_number || "",
    });
  }

  private applyQuiz(data: any): void {
    if (!data.quiz_result) return;
    const quiz =
      typeof data.quiz_result === "string" ? JSON.parse(data.quiz_result) : data.quiz_result;
    this.quizForm.patchValue({
      // Canonical key is userInformation; fall back to the legacy fishType value.
      userInformation: quiz.userInformation || quiz.fishType || "",
      tankCapacity: quiz.tankCapacity || "",
    });
  }

  private applyRemarks(data: any): void {
    this.remarksForm.patchValue({
      adminRemarks: data.admin_remarks || "",
      adminFollowup: data.admin_followup || "",
    });
  }

  /** After a save: the reply's updated_on (the saved section is applied by its caller). */
  private applySaved(data: any): void {
    if (data?.updated_on) this.updated_on.set(data.updated_on);
  }

  saveContactInfo(): void {
    if (this.contactForm.invalid) {
      this.showMessage("Please fill all required fields correctly");
      return;
    }

    const formValue = this.contactForm.value;
    const payload = {
      userId: this.id(),
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phoneCountryCode: formValue.phoneCountryCode,
      phoneNumber: formValue.phoneNumber,
      iso2: formValue.iso2 || null,
    };

    this.isSaving.set(true);
    this.auth.patch("/api/v1/user", payload).subscribe({
      next: (res: any) => {
        if (res.result_code === 1) {
          this.showMessage("Contact information updated successfully");
          // Country is not in the reply; the form already holds what was saved.
          if (res.data) this.applyContact(res.data);
          this.applySaved(res.data);
        } else {
          this.showMessage(res.message || "Update failed");
        }
        this.isSaving.set(false);
      },
      error: (error) => {
        console.error("Error updating contact info:", error);
        this.showMessage("Error updating contact information");
        this.isSaving.set(false);
      },
    });
  }

  saveQuizInfo(): void {
    const formValue = this.quizForm.value;
    const payload = {
      userId: this.id(),
      userInformation: formValue.userInformation,
      tankCapacity: formValue.tankCapacity,
    };

    this.isSaving.set(true);
    this.auth.patch("/api/v1/user", payload).subscribe({
      next: (res: any) => {
        if (res.result_code === 1) {
          this.showMessage("Quiz information updated successfully");
          if (res.data) this.applyQuiz(res.data);
          this.applySaved(res.data);
        } else {
          this.showMessage(res.message || "Update failed");
        }
        this.isSaving.set(false);
      },
      error: (error) => {
        console.error("Error updating quiz info:", error);
        this.showMessage("Error updating quiz information");
        this.isSaving.set(false);
      },
    });
  }

  toggleBadge(slug: string, on: boolean): void {
    this.badges.update((list) =>
      on ? [...new Set([...list, slug])] : list.filter((b) => b !== slug)
    );
  }

  saveBadges(): void {
    this.isSaving.set(true);
    this.auth.patch("/api/v1/user", { userId: this.id(), badges: this.badges() }).subscribe({
      next: (res: any) => {
        if (res.result_code === 1) {
          this.showMessage("Badges updated");
          // The badges shown are the ones just saved.
          this.applySaved(res.data);
        } else {
          this.showMessage(res.message || "Update failed");
        }
        this.isSaving.set(false);
      },
      error: (error) => {
        console.error("Error updating badges:", error);
        this.showMessage("Error updating badges");
        this.isSaving.set(false);
      },
    });
  }

  saveRemarks(): void {
    const formValue = this.remarksForm.value;
    const payload = {
      userId: this.id(),
      adminRemarks: formValue.adminRemarks,
      adminFollowup: formValue.adminFollowup,
    };

    this.isSaving.set(true);
    this.auth.patch("/api/v1/user", payload).subscribe({
      next: (res: any) => {
        if (res.result_code === 1) {
          this.showMessage("Remarks updated successfully");
          if (res.data) this.applyRemarks(res.data);
          this.applySaved(res.data);
        } else {
          this.showMessage(res.message || "Update failed");
        }
        this.isSaving.set(false);
      },
      error: (error) => {
        console.error("Error updating remarks:", error);
        this.showMessage("Error updating remarks");
        this.isSaving.set(false);
      },
    });
  }

  fetchOrders(): void {
    this.isLoadingOrders.set(true);
    this.auth
      .get<IApiResponse>(
        `/api/v2/orders?userId=${this.id()}&pageIndex=0&pageSize=100&sortProp=created_on&sortDir=desc`
      )
      .subscribe({
        next: (res: any) => {
          if (res.result_code === 1) {
            this.orders.set(res.data?.list || []);
          }
          this.isLoadingOrders.set(false);
        },
        error: () => {
          this.isLoadingOrders.set(false);
        },
      });
  }

  goToOrder(orderId: string): void {
    this.router.navigate(["/order_details", orderId]);
  }

  formatOrderColumn(key: string, value: any): string {
    const moneyKeys = [
      "sub_total",
      "shipping_total",
      "destination_transhipper_markup",
      "offerDiscountAmount",
      "deliveryDiscountAmount",
      "total",
    ];
    if (moneyKeys.includes(key)) return `$${Number(value || 0).toFixed(2)}`;
    if (key === "created_on") return value ? new Date(value).toLocaleString("en-US") : "—";
    if (key === "payment_type") {
      const labels: Record<string, string> = {
        card: "Credit Card",
        apple_pay: "Apple Pay",
        google_pay: "Google Pay",
        paynow: "PayNow",
        grabpay: "GrabPay",
        link: "Link",
      };
      return labels[value] || value || "—";
    }
    if (key === "order_status") return value === "in_progress" ? "In Progress" : value || "—";
    return value ?? "—";
  }

  orderColumnHeader(key: string): string {
    const headers: Record<string, string> = {
      id: "Order ID",
      iso2: "Country",
      currency: "Currency",
      sub_total: "Subtotal",
      shipping_total: "Shipping",
      destination_transhipper_markup: "DTM",
      offerDiscountAmount: "Offer Discount",
      deliveryDiscountAmount: "Delivery Discount",
      total: "Total",
      created_on: "Created",
      payment_type: "Payment Type",
      payment_status: "Payment Status",
      order_status: "Order Status",
    };
    return headers[key] || key;
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, "Close", {
      duration: 3000,
      horizontalPosition: "end",
      verticalPosition: "top",
    });
  }
}

/**
 * The six fishkeeper identity badges, shown as coloured circles on the
 * shopper's storefront profile (web-frontend lib/config/fishkeeper-badges.ts
 * has the same slugs, colours and matching icons; admin-backend refuses any
 * other slug). Icons here are the closest Material Icons.
 */
export const FISHKEEPER_BADGES = [
  { slug: "rare_fish_hunter", title: "Rare Fish Hunter", icon: "diamond", color: "#534AB7" },
  {
    slug: "breeder_genetics",
    title: "Breeder & Genetics Enthusiast",
    icon: "biotech",
    color: "#993556",
  },
  { slug: "aquascaper", title: "Aquascaper", icon: "eco", color: "#3B6D11" },
  {
    slug: "dedicated_fishkeeper",
    title: "Dedicated Fishkeeper",
    icon: "verified_user",
    color: "#185FA5",
  },
  {
    slug: "casual_enthusiast",
    title: "Casual Enthusiast",
    icon: "sentiment_satisfied",
    color: "#B45309",
  },
  {
    slug: "multi_tank_power_user",
    title: "Multi-Tank Power User",
    icon: "grid_view",
    color: "#475569",
  },
] as const;

/** One search on this shopper's Fish Hunt list, as the API returns it. */
export interface FishHunt {
  query: string;
  iso2: string;
  created_at: string;
}

/** One row of this shopper's search history, as the API returns it. */
export interface RecentSearch {
  query: string;
  iso2: string | null;
  created_at: string;
}

export interface IUserQuizResponse {
  experienceLevel: string;
  aquariumBestDescription: string;
  /** Formerly "fishType" — now holds the user's information / Fishyhub AI responses. */
  userInformation: string;
  /** @deprecated legacy key, read for backward compatibility */
  fishType?: string;
  tankCapacity: string;
}

export interface IUserCartApiResponse {
  status: boolean;
  result_code: number;
  message: string;
  data: IUserCartApiData;
}

export interface IUserCartApiData {
  /** The cart as the customer sees it (web-backend builds it), or null. */
  cart: CustomerCartView | null;
}

export interface IOrderRow {
  id: string;
  iso2: string;
  currency: string;
  sub_total: number;
  shipping_total: number;
  destination_transhipper_markup: number;
  offerDiscountAmount: number;
  deliveryDiscountAmount: number;
  total: number;
  created_on: string;
  payment_type: string;
  payment_status: string;
  order_status: string;
}
